import { useMemo } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import type { BudgetData, PhaseCalendarNeighborBounds } from "../components/GuidedBudgetEditor";
import {
  META_PHASE_MIN_GAP_MINUTES,
  type ScheduledBudgetAdFlightUtcBounds,
  wallMinuteToDayjsInEventZone,
  wireScheduleToEventWall,
} from "../utils/eventWallTime";
import { getScheduledBudgetPhaseTimelineError } from "../utils/scheduledBudgetPhaseTimelineValidation";

dayjs.extend(utc);
dayjs.extend(timezone);

type UseGuidedBudgetScheduledMetaArgs = {
  isScheduled: boolean;
  budget?: BudgetData;
  eventTimezone: string;
  scheduledBudgetFlightBounds: ScheduledBudgetAdFlightUtcBounds | null;
  externalScheduledPhaseTimelineMessage: string | undefined;
};

export function useGuidedBudgetScheduledMeta({
  isScheduled,
  budget,
  eventTimezone,
  scheduledBudgetFlightBounds,
  externalScheduledPhaseTimelineMessage,
}: UseGuidedBudgetScheduledMetaArgs) {
  const implicitFlightWalls = useMemo(() => {
    const fb = scheduledBudgetFlightBounds;
    if (!fb?.flightStartIso?.trim() || !fb?.flightEndIso?.trim()) return null;
    const tz = eventTimezone.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    return {
      firstPhaseStartWall: wireScheduleToEventWall(fb.flightStartIso, tz),
      lastPhaseEndWall: wireScheduleToEventWall(fb.flightEndIso, tz),
    };
  }, [scheduledBudgetFlightBounds, eventTimezone]);

  const flightRangeInEventZone = useMemo((): {
    start: Dayjs;
    end: Dayjs;
  } | null => {
    const fs = scheduledBudgetFlightBounds?.flightStartIso?.trim();
    const fe = scheduledBudgetFlightBounds?.flightEndIso?.trim();
    if (!fs || !fe) return null;
    const tz = eventTimezone.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    const start = dayjs(fs).tz(tz);
    const end = dayjs(fe).tz(tz);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;
    return { start, end };
  }, [scheduledBudgetFlightBounds, eventTimezone]);

  const internalScheduledPhaseTimelineError = useMemo(
    () =>
      externalScheduledPhaseTimelineMessage !== undefined
        ? null
        : isScheduled
          ? getScheduledBudgetPhaseTimelineError(
              budget?.segments,
              eventTimezone,
              implicitFlightWalls
            )
          : null,
    [
      externalScheduledPhaseTimelineMessage,
      isScheduled,
      budget?.segments,
      eventTimezone,
      implicitFlightWalls,
    ]
  );

  const scheduledPhaseTimelineError =
    externalScheduledPhaseTimelineMessage !== undefined
      ? externalScheduledPhaseTimelineMessage || null
      : internalScheduledPhaseTimelineError;

  /** Keeps each phase’s pickers from crossing the previous / next segment (contiguous chain). */
  const phaseNeighborPickerBounds = useMemo((): Array<
    PhaseCalendarNeighborBounds | null
  > => {
    if (!isScheduled) return [];
    const segs = budget?.segments ?? [];
    const tz = eventTimezone.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    return segs.map((_, index) => {
      const prev = index > 0 ? segs[index - 1] : undefined;
      const next = index + 1 < segs.length ? segs[index + 1] : undefined;
      let startMin: Dayjs | undefined;
      if (prev?.time_end?.trim()) {
        const d = wallMinuteToDayjsInEventZone(prev.time_end, tz);
        if (d?.isValid()) startMin = d;
      }
      let endMax: Dayjs | undefined;
      if (next?.time_end?.trim()) {
        const dEnd = wallMinuteToDayjsInEventZone(next.time_end, tz);
        if (dEnd?.isValid()) {
          const capped = dEnd.subtract(META_PHASE_MIN_GAP_MINUTES, "minute");
          if (capped.isValid()) endMax = capped;
        }
      }
      if (startMin == null && endMax == null) return null;
      const o: PhaseCalendarNeighborBounds = {};
      if (startMin) o.startMin = startMin;
      if (endMax) o.endMax = endMax;
      return o;
    });
  }, [isScheduled, budget?.segments, eventTimezone]);

  return {
    flightRangeInEventZone,
    scheduledPhaseTimelineError,
    phaseNeighborPickerBounds,
  };
}
